/**
 * Shopify tab — single sidebar entry under Platforms.
 * Internal tabs (Overview / Orders / Customers / Products / Abandoned)
 * mirror the Avaya/Calls pattern: KPIs + chart up top, tab strip below,
 * content area swaps based on the active tab.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  BarChart3, ShoppingBag, Package, Users as UsersIcon, Box,
  RefreshCw, DollarSign, ShoppingCart, TrendingDown, ExternalLink,
  Search, ChevronLeft, ChevronRight,
  ArrowUp, ArrowDown, Crown, MapPin, TrendingUp, Award,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip, LabelList,
} from 'recharts';
import { useToast } from './ToastContainer';
import api from '../utils/api';
import ShopifyOrders from './ShopifyOrders';
import ShopifyCustomers from './ShopifyCustomers';
import ShopifyProducts from './ShopifyProducts';
import './Shopify.css';

const PERIODS = [
  { value: '7d',   label: '7 days' },
  { value: '30d',  label: '30 days' },
  { value: '90d',  label: '90 days' },
  { value: '365d', label: '1 year' },
];

const TABS = [
  { value: 'overview',  label: 'Overview',  icon: BarChart3 },
  { value: 'orders',    label: 'Orders',    icon: Package },
  { value: 'customers', label: 'Customers', icon: UsersIcon },
  { value: 'products',  label: 'Products',  icon: Box },
  { value: 'abandoned', label: 'Abandoned', icon: ShoppingCart },
];

const moneyFmt = (amount, code) => {
  if (amount == null) return '—';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency: code || 'USD', maximumFractionDigits: 0,
    }).format(Number(amount));
  } catch {
    return `${code || ''} ${Number(amount).toLocaleString()}`;
  }
};
const intFmt = (n) => (n == null ? '—' : Number(n).toLocaleString());
const pctFmt = (n) => (n == null ? '—' : `${(Number(n) * 100).toFixed(1)}%`);
const relativeTime = (iso) => {
  if (!iso) return 'never';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return 'just now';
  if (ms < 3_600_000) return `${Math.round(ms / 60_000)} min ago`;
  if (ms < 86_400_000) return `${Math.round(ms / 3_600_000)} h ago`;
  return `${Math.round(ms / 86_400_000)} d ago`;
};

const Shopify = () => {
  const { showError, showSuccess } = useToast();

  const [stores, setStores] = useState([]);
  const [selectedStoreId, setSelectedStoreId] = useState(null);
  const [period, setPeriod] = useState('30d');
  const [activeTab, setActiveTab] = useState('overview');

  const [overview, setOverview] = useState(null);
  const [syncJobs, setSyncJobs] = useState([]);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [triggering, setTriggering] = useState(false);

  // Stores list — fetched once
  useEffect(() => {
    api.get('/api/shopify/stores')
      .then(res => setStores(res.data.stores || []))
      .catch(e => showError('Failed to load stores: ' + (e.response?.data?.error || e.message)));
  }, [showError]);

  // Overview KPIs + sync status — refetched whenever store/period changes
  const loadHeader = useCallback(async () => {
    const storeQs = selectedStoreId ? `&storeId=${selectedStoreId}` : '';
    const settled = await Promise.allSettled([
      api.get(`/api/shopify/overview?period=${period}${storeQs}`),
      api.get(`/api/shopify/sync-status?${storeQs.replace('&', '')}`),
    ]);
    if (settled[0].status === 'fulfilled') setOverview(settled[0].value.data);
    if (settled[1].status === 'fulfilled') setSyncJobs(settled[1].value.data.jobs || []);
  }, [selectedStoreId, period]);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    loadHeader().finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [loadHeader]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadHeader();
    setRefreshing(false);
  };

  const handleTriggerBackfill = async () => {
    if (!selectedStoreId) { showError('Pick a specific store first'); return; }
    if (!window.confirm('Queue a full backfill for this store? Railways will pick it up on next run.')) return;
    setTriggering(true);
    try {
      const res = await api.post('/api/shopify/trigger-backfill', { storeId: selectedStoreId });
      showSuccess(res.data.message || 'Backfill queued');
      await loadHeader();
    } catch (e) {
      showError('Trigger failed: ' + (e.response?.data?.error || e.message));
    } finally {
      setTriggering(false);
    }
  };

  const selectedStore = useMemo(
    () => stores.find(s => s.id === selectedStoreId) || null,
    [stores, selectedStoreId]
  );
  const activeJob = useMemo(
    () => syncJobs.find(j => j.status === 'pending' || j.status === 'running') || null,
    [syncJobs]
  );
  const lastSync = useMemo(() => {
    if (selectedStore?.last_incremental_sync_at) return selectedStore.last_incremental_sync_at;
    if (selectedStore?.last_full_backfill_at) return selectedStore.last_full_backfill_at;
    return null;
  }, [selectedStore]);

  const currency = overview?.currency || selectedStore?.currency_code || 'SAR';

  if (loading) {
    return (
      <div className="shop-loading">
        <div className="shop-spinner" />
        <p>Loading Shopify data…</p>
      </div>
    );
  }

  return (
    <div className="shop">
      {/* Header: title + store/period selectors + refresh */}
      <div className="shop-header">
        <div>
          <h1 className="shop-title">Shopify</h1>
          <p className="shop-subtitle">Sales, customers, and order signals across your stores</p>
        </div>
        <div className="shop-header-actions">
          <select
            className="shop-select"
            value={selectedStoreId || ''}
            onChange={(e) => setSelectedStoreId(e.target.value || null)}
          >
            <option value="">All stores ({stores.length})</option>
            {stores.map(s => (
              <option key={s.id} value={s.id}>{s.display_name || s.store_handle}</option>
            ))}
          </select>
          <div className="shop-period-toggle">
            {PERIODS.map(p => (
              <button
                key={p.value}
                className={`shop-period-btn ${period === p.value ? 'active' : ''}`}
                onClick={() => setPeriod(p.value)}
              >{p.label}</button>
            ))}
          </div>
          <button className="shop-refresh" onClick={handleRefresh} disabled={refreshing}>
            <RefreshCw size={14} className={refreshing ? 'shop-spin' : ''} />
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Sync status / backfill trigger */}
      <div className="shop-sync-bar">
        <div className="shop-sync-info">
          {activeJob ? (
            <>
              <span className="shop-sync-dot active" />
              <span><strong>{activeJob.job_type.replace('_', ' ')}</strong> {activeJob.status}
                {' · '}started {relativeTime(activeJob.started_at || activeJob.created_at)}
                {activeJob.counters && Object.keys(activeJob.counters).length > 0 && (
                  <> · {Object.entries(activeJob.counters).map(([key, v]) => `${key}: ${intFmt(v)}`).join(', ')}</>
                )}
              </span>
            </>
          ) : (
            <>
              <span className="shop-sync-dot" />
              <span>Last synced {relativeTime(lastSync)}</span>
            </>
          )}
        </div>
        {selectedStoreId && (
          <button
            className="shop-action-btn"
            onClick={handleTriggerBackfill}
            disabled={triggering || !!activeJob}
            title={activeJob ? 'A sync is already in progress' : 'Queue a full backfill for this store'}
          >
            {triggering ? 'Queueing…' : (activeJob ? 'Sync running…' : 'Trigger backfill')}
          </button>
        )}
      </div>

      {/* Tab strip */}
      <div className="shop-tabs">
        {TABS.map(t => (
          <button
            key={t.value}
            className={`shop-tab ${activeTab === t.value ? 'active' : ''}`}
            onClick={() => setActiveTab(t.value)}
          >
            <t.icon size={16} /> {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'overview' && (
        <OverviewTab overview={overview} period={period} currency={currency} />
      )}
      {activeTab === 'orders' && (
        <ShopifyOrders storeId={selectedStoreId} />
      )}
      {activeTab === 'customers' && (
        <ShopifyCustomers storeId={selectedStoreId} />
      )}
      {activeTab === 'products' && (
        <ShopifyProducts storeId={selectedStoreId} period={period} currency={currency} />
      )}
      {activeTab === 'abandoned' && (
        <AbandonedTab storeId={selectedStoreId} />
      )}
    </div>
  );
};

const TIER_COLORS = { Gold: '#fbbf24', Silver: '#cbd5e1', Bronze: '#fb923c', None: '#475569' };
const DOW_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const OverviewTab = ({ overview, period, currency }) => {
  if (!overview) return <div className="shop-empty">No data yet — try refreshing.</div>;
  const k = overview.kpis || {};
  return (
    <>
      {/* KPI strip — 6 cards, each with period-over-period delta */}
      <div className="shop-kpis shop-kpis-6">
        <KpiCard icon={<DollarSign size={16} />}    label={`Revenue (${period})`}   k={k.revenue}        format={(v) => moneyFmt(v, currency)} accent="green" delay={0.05} />
        <KpiCard icon={<ShoppingBag size={16} />}   label="Orders"                  k={k.ordersCount}    format={intFmt}                       accent="blue"  delay={0.10} />
        <KpiCard icon={<TrendingUp size={16} />}    label="Avg order value"         k={k.aov}            format={(v) => moneyFmt(v, currency)} accent="cyan"  delay={0.15} />
        <KpiCard icon={<TrendingDown size={16} />}  label="Refund rate"             k={k.refundRate}     format={pctFmt}                       accent={(k.refundRate?.value || 0) > 0.05 ? 'red' : 'neutral'} delay={0.20} invertDelta />
        <KpiCard icon={<UsersIcon size={16} />}     label="Customers"               k={k.customersCount} format={intFmt}                       accent="purple" delay={0.25} />
        <KpiCard icon={<ShoppingCart size={16} />}  label="Abandoned $"             k={k.abandonedValue} format={(v) => moneyFmt(v, currency)} accent="amber" delay={0.30} invertDelta />
      </div>

      {/* Revenue trend (full width) */}
      <div className="shop-panel">
        <div className="shop-panel-head">
          <h3>Revenue trend</h3>
          <span className="shop-panel-meta">{period} · daily</span>
        </div>
        <div className="shop-chart-box">
          {overview.revenueByDay?.length ? (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={overview.revenueByDay}>
                <defs>
                  <linearGradient id="shopRev" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%"   stopColor="#10b981" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                <XAxis dataKey="date" tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 11 }} />
                <YAxis tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 11 }} />
                <Tooltip contentStyle={chartTooltipStyle} formatter={(v) => moneyFmt(v, currency)} />
                <Area type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={2} fill="url(#shopRev)" />
              </AreaChart>
            </ResponsiveContainer>
          ) : <div className="shop-empty">No revenue data in this window.</div>}
        </div>
      </div>

      {/* Row: VIP mix donut + Top cities bar (2-col) */}
      <div className="shop-grid-2">
        <div className="shop-panel">
          <div className="shop-panel-head">
            <h3><Crown size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />VIP tier mix</h3>
            <span className="shop-panel-meta">by revenue</span>
          </div>
          <div className="shop-chart-box" style={{ height: 240 }}>
            {(overview.vipMix || []).length ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={overview.vipMix.filter(t => t.revenue > 0)}
                    dataKey="revenue" nameKey="tier"
                    innerRadius={50} outerRadius={85}
                    paddingAngle={3}
                  >
                    {overview.vipMix.map((t) => <Cell key={t.tier} fill={TIER_COLORS[t.tier] || '#475569'} />)}
                  </Pie>
                  <Tooltip contentStyle={chartTooltipStyle} formatter={(v) => moneyFmt(v, currency)} />
                </PieChart>
              </ResponsiveContainer>
            ) : <div className="shop-empty">No tier data.</div>}
          </div>
          <div className="shop-tier-legend">
            {(overview.vipMix || []).map(t => (
              <div key={t.tier} className="shop-tier-row">
                <span className="shop-tier-dot" style={{ background: TIER_COLORS[t.tier] || '#475569' }} />
                <span className="shop-tier-name">{t.tier}</span>
                <span className="shop-tier-customers">{intFmt(t.customers)} cust</span>
                <span className="shop-tier-revenue">{moneyFmt(t.revenue, currency)}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="shop-panel">
          <div className="shop-panel-head">
            <h3><MapPin size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />Top cities</h3>
            <span className="shop-panel-meta">by revenue</span>
          </div>
          <div className="shop-chart-box" style={{ height: 280 }}>
            {(overview.topCities || []).length ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={overview.topCities} layout="vertical" margin={{ left: 80, right: 50 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" horizontal={false} />
                  <XAxis type="number" tick={{ fill: 'rgba(255,255,255,0.6)', fontSize: 11 }} />
                  <YAxis dataKey="city" type="category" tick={{ fill: 'rgba(255,255,255,0.85)', fontSize: 12 }} width={80} />
                  <Tooltip contentStyle={chartTooltipStyle} formatter={(v) => moneyFmt(v, currency)} />
                  <Bar dataKey="revenue" fill="#3b82f6" radius={[0, 4, 4, 0]}>
                    <LabelList dataKey="orders" position="right" fill="rgba(255,255,255,0.55)" fontSize={11} formatter={(v) => `${v} ord`} />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : <div className="shop-empty">No city tags found.</div>}
          </div>
        </div>
      </div>

      {/* Heatmap: day-of-week × hour-of-day revenue */}
      <div className="shop-panel">
        <div className="shop-panel-head">
          <h3>When do customers buy?</h3>
          <span className="shop-panel-meta">day × hour · revenue intensity (UTC)</span>
        </div>
        <Heatmap matrix={overview.heatmap || []} currency={currency} />
      </div>

      {/* Row: Top products + Recent refunds (2-col) */}
      <div className="shop-grid-2">
        <div className="shop-panel">
          <div className="shop-panel-head">
            <h3><Award size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />Top products</h3>
            <span className="shop-panel-meta">{period}</span>
          </div>
          {(overview.topProducts || []).length === 0 ? (
            <div className="shop-empty">No sales in this window.</div>
          ) : (
            <table className="shop-table">
              <thead>
                <tr><th>#</th><th>Product</th><th>Units</th><th style={{ textAlign: 'right' }}>Revenue</th></tr>
              </thead>
              <tbody>
                {overview.topProducts.map((p, i) => (
                  <tr key={p.key}>
                    <td style={{ color: 'rgba(255,255,255,0.4)' }}>{i + 1}</td>
                    <td>
                      <div>{p.title || '—'}</div>
                      {p.sku && <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.4)', fontFamily: 'monospace' }}>{p.sku}</div>}
                    </td>
                    <td>{intFmt(p.units)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600, color: '#10b981' }}>{moneyFmt(p.revenue, currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="shop-panel">
          <div className="shop-panel-head">
            <h3><TrendingDown size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />Recent refunds</h3>
            <span className="shop-panel-meta">last {(overview.recentRefunds || []).length}</span>
          </div>
          {(overview.recentRefunds || []).length === 0 ? (
            <div className="shop-empty">No refunds in this window.</div>
          ) : (
            <div className="shop-cart-list">
              {overview.recentRefunds.map(r => (
                <div key={r.id} className="shop-cart-row">
                  <div className="shop-cart-customer">
                    <div className="shop-cart-name">
                      <Link to={`/shopify/orders/${r.orderId}`} className="shop-order-link">{r.orderName}</Link>
                    </div>
                    <div className="shop-cart-meta">{r.customer || '—'}</div>
                  </div>
                  <div className="shop-cart-value" style={{ color: '#f87171' }}>−{moneyFmt(r.amount, r.currency || currency)}</div>
                  <div className="shop-cart-time">{relativeTime(r.createdAt)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

// Day-of-week × hour-of-day heatmap (CSS grid). Cell color intensity scales
// against the matrix max; tooltip on hover shows the formatted amount.
const Heatmap = ({ matrix, currency }) => {
  if (!matrix.length) return <div className="shop-empty">No data for heatmap.</div>;
  let max = 0;
  for (const row of matrix) for (const v of row) if (v > max) max = v;
  const cell = (v, dow, hour) => {
    const intensity = max > 0 ? v / max : 0;
    const bg = intensity === 0
      ? 'rgba(255,255,255,0.03)'
      : `rgba(16, 185, 129, ${0.12 + intensity * 0.78})`;
    const titleStr = v > 0 ? `${DOW_LABELS[dow]} ${hour}:00 — ${moneyFmt(v, currency)}` : `${DOW_LABELS[dow]} ${hour}:00 — no orders`;
    return <div key={`${dow}-${hour}`} className="shop-heatmap-cell" style={{ background: bg }} title={titleStr} />;
  };
  return (
    <div className="shop-heatmap-wrap">
      <div className="shop-heatmap-corner" />
      <div className="shop-heatmap-hours">
        {Array.from({ length: 24 }).map((_, h) => (
          <div key={h} className="shop-heatmap-hour-label">
            {h % 3 === 0 ? `${h}` : ''}
          </div>
        ))}
      </div>
      {matrix.map((row, dow) => (
        <React.Fragment key={dow}>
          <div className="shop-heatmap-dow-label">{DOW_LABELS[dow]}</div>
          <div className="shop-heatmap-row">
            {row.map((v, h) => cell(v, dow, h))}
          </div>
        </React.Fragment>
      ))}
    </div>
  );
};

const chartTooltipStyle = {
  background: 'rgba(20,20,25,0.95)',
  border: '1px solid rgba(255,255,255,0.12)',
  borderRadius: 8,
  color: '#fff',
  fontSize: 12,
};

const AbandonedTab = ({ storeId }) => {
  const { showError } = useToast();
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [onlyUnrecovered, setOnlyUnrecovered] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => { setPage(1); }, [storeId, onlyUnrecovered, search]);
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ page, limit: pageSize, onlyUnrecovered: String(onlyUnrecovered) });
    if (storeId) params.set('storeId', storeId);
    if (search) params.set('search', search);
    api.get(`/api/shopify/abandoned?${params}`)
      .then(r => {
        setRows(r.data.checkouts || []);
        setPagination(r.data.pagination || { total: 0, totalPages: 1 });
      })
      .catch(e => showError('Failed to load abandoned: ' + (e.response?.data?.error || e.message)))
      .finally(() => setLoading(false));
  }, [storeId, onlyUnrecovered, search, page, pageSize, showError]);

  return (
    <div className="shop-panel">
      <div className="shop-tab-toolbar">
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem', color: 'rgba(255,255,255,0.85)' }}>
          <input type="checkbox" checked={onlyUnrecovered} onChange={(e) => setOnlyUnrecovered(e.target.checked)} />
          Only unrecovered
        </label>
        <div style={{ position: 'relative', flex: 1, maxWidth: 320 }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.4)' }} />
          <input
            className="shop-select"
            style={{ paddingLeft: 32, width: '100%' }}
            placeholder="Search email or customer…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <div className="shop-loading" style={{ minHeight: 200 }}><div className="shop-spinner" /></div>
      ) : rows.length === 0 ? (
        <div className="shop-empty">
          <ShoppingCart size={32} style={{ opacity: 0.4 }} />
          <div style={{ marginTop: '0.5rem' }}>
            {search ? `No abandoned checkouts matching "${search}".` : 'No abandoned checkouts.'}
          </div>
        </div>
      ) : (
        <>
          <div className="shop-cart-list">
            {rows.map(a => (
              <div key={a.id} className="shop-cart-row">
                <div className="shop-cart-customer">
                  <div className="shop-cart-name">{a.customer_display_name || a.customer_email || 'Anonymous'}</div>
                  <div className="shop-cart-meta">
                    {(a.line_items || []).slice(0, 3).map(li => li.title).join(', ')}
                    {(a.line_items || []).length > 3 ? ` +${a.line_items.length - 3} more` : ''}
                  </div>
                </div>
                <div className="shop-cart-value">{moneyFmt(a.total_price, a.currency_code)}</div>
                <div className="shop-cart-time">{relativeTime(a.shopify_created_at)}</div>
                {a.recovery_url && (
                  <a className="shop-cart-link" href={a.recovery_url} target="_blank" rel="noreferrer">
                    Recover <ExternalLink size={12} />
                  </a>
                )}
              </div>
            ))}
          </div>

          <div className="shop-pagination">
            <div>
              Show <select className="shop-select" style={{ display: 'inline-block', padding: '0.25rem 0.5rem' }} value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select> per page
            </div>
            <div className="shop-pagination-controls">
              <button className="shop-page-btn" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><ChevronLeft size={14} /> Prev</button>
              <span className="shop-page-info">Page {page} of {pagination.totalPages || 1}</span>
              <button className="shop-page-btn" disabled={page >= (pagination.totalPages || 1)} onClick={() => setPage(p => p + 1)}>Next <ChevronRight size={14} /></button>
            </div>
            <div className="shop-page-total">{pagination.total.toLocaleString()} abandoned total</div>
          </div>
        </>
      )}
    </div>
  );
};

// KPI card with period-over-period delta. `k` is { value, prev, delta } from
// the API. `format` is how the value/prev get rendered (currency, int, pct).
// `invertDelta` flips the green/red logic — for refunds and abandoned, an
// increase is bad, so a positive delta should appear red, not green.
const KpiCard = ({ icon, label, k, format, accent = 'neutral', delay = 0, invertDelta = false }) => {
  const v = k?.value ?? 0;
  const prev = k?.prev;
  const delta = k?.delta;
  let deltaUI = null;
  if (delta != null) {
    const up = delta > 0;
    const meaningfullyChanged = Math.abs(delta) >= 0.001;
    const positive = invertDelta ? !up : up;
    const cls = !meaningfullyChanged ? 'flat' : (positive ? 'up' : 'down');
    deltaUI = (
      <span className={`shop-kpi-delta ${cls}`}>
        {meaningfullyChanged ? (up ? <ArrowUp size={11} /> : <ArrowDown size={11} />) : null}
        {meaningfullyChanged ? `${(Math.abs(delta) * 100).toFixed(1)}%` : 'flat'}
      </span>
    );
  }
  return (
    <motion.div
      className="shop-kpi"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
    >
      <div className="shop-kpi-head">
        <span className={`shop-kpi-icon ${accent}`}>{icon}</span>
        {label}
      </div>
      <div className={`shop-kpi-value ${accent}`}>{format ? format(v) : v}</div>
      <div className="shop-kpi-sub">
        {deltaUI}
        {prev != null && <span className="shop-kpi-prev">vs {format ? format(prev) : prev} prev</span>}
      </div>
    </motion.div>
  );
};

export default Shopify;
